import { memo, type RefObject, useCallback, useState } from 'react';

import { useTrackBoardTemplatesGenerated } from '@air/analytics';
import { useToasts } from '@air/provider-toast';

import { BoardTemplateModalGenerateInput } from '~/components/Modals/BoardTemplateModal/BoardTemplateModalGenerateInput';
import { BoardTemplateModalGeneratePreview } from '~/components/Modals/BoardTemplateModal/BoardTemplateModalGeneratePreview';
import { countBoards } from '~/components/Modals/BoardTemplateModal/boardTemplateHelpers';
import { type BoardTemplate } from '~/components/Modals/BoardTemplateModal/types';
import { useBoardTemplateMutation } from '~/swr-hooks/workspaces/useBoardTemplateMutation';

export type BoardTemplateModalGenerateTabProps = {
  workspaceId: string;
  isCreating: boolean;
  generatedBoards: BoardTemplate[] | null;
  onBoardsGenerated: (boards: BoardTemplate[], prompt: string) => void;
  textareaRef?: RefObject<HTMLTextAreaElement>;
};

export const BoardTemplateModalGenerateTab = memo(
  ({
    workspaceId,
    isCreating,
    generatedBoards,
    onBoardsGenerated,
    textareaRef,
  }: BoardTemplateModalGenerateTabProps) => {
    const [description, setDescription] = useState('');
    const { showToast } = useToasts();
    const { mutate: generateBoardStructure, isPending: isGenerating } = useBoardTemplateMutation();
    const { trackBoardTemplatesGenerated } = useTrackBoardTemplatesGenerated();

    const onGenerate = useCallback(() => {
      generateBoardStructure(
        {
          workspaceId,
          description,
        },
        {
          onSuccess: (response) => {
            const boardCount = countBoards(response.boards);
            trackBoardTemplatesGenerated({ prompt: description, number_of_boards: boardCount });
            onBoardsGenerated(response.boards, description);
          },
          onError: () => {
            showToast('Failed to generate board structure. Please try again.', { type: 'assertive' });
            onBoardsGenerated([], '');
          },
        },
      );
    }, [description, workspaceId, generateBoardStructure, onBoardsGenerated, showToast, trackBoardTemplatesGenerated]);

    return (
      <div className="flex min-h-[400px] gap-6 py-4">
        <BoardTemplateModalGenerateInput
          description={description}
          onDescriptionChange={setDescription}
          onGenerate={onGenerate}
          isGenerating={isGenerating}
          isCreating={isCreating}
          hasGeneratedBoards={!!generatedBoards}
          textareaRef={textareaRef}
        />
        <BoardTemplateModalGeneratePreview isGenerating={isGenerating} generatedBoards={generatedBoards} />
      </div>
    );
  },
);

BoardTemplateModalGenerateTab.displayName = 'BoardTemplateModalGenerateTab';
